import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApiQuery } from '../../../../../../api/hooks';
import apiClient from '../../../../../../api/apiClient';
import { useConfirmationModalStore, usePreloaderStore } from '../../../../../../stores';
import { Endpoints, Defaults, QuestionType, BusinessErrorCode } from '../../../../../../views/democracy/constants';
import { secondsToHHMMSS, extractErrorMessage, buildMediaUrl } from '../../../../../../views/democracy/utils';
import { EditPencilIcon, TrashIcon, ChevronDownIcon, PlusIcon, DragDotsIcon } from '../../../../model/icons';
import QuestionEditModal from './QuestionEditModal';

function pluralQuestions(n) {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'вопрос';
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'вопроса';
    return 'вопросов';
}

// Раунд (.round): строка-заголовок + раскрывающаяся деталь с таблицей вопросов.
export default function RoundItem({ round, questionCount, expanded, onToggle, onEditRound, onChanged, dragHandleProps }) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { open: openConfirmation } = useConfirmationModalStore();
    const { show, hide } = usePreloaderStore();
    const [selectedQuestion, setSelectedQuestion] = useState(null);

    const questionsKey = ['democracy', 'questions', round.id];
    const { data: rawQuestions = [] } = useApiQuery(
        questionsKey,
        Endpoints.questions.listForRound(round.id),
        { refetchOnMount: 'always', enabled: expanded }
    );
    const questions = (rawQuestions || []).filter(q => q && q.id != null).sort((a, b) => (a.orderId || 0) - (b.orderId || 0));
    // Раскрыт — берём свежее число из запроса вопросов; свёрнут — из счётчика родителя (полная демократия).
    const displayCount = expanded ? questions.length : questionCount;

    const timeLabel = secondsToHHMMSS(round.timeBudgetSeconds ?? Defaults.timeBudgetSeconds);
    const coverLabel = round.coverMedia?.fileName || round.coverMedia?.url?.split('/')?.pop() || null;
    const coverUrl = round.coverMedia
        ? (round.coverMedia.mediaFileUrl || (round.coverMedia.url ? buildMediaUrl(round.coverMedia.url) : null))
        : null;

    const invalidateQuestions = () => {
        queryClient.invalidateQueries({ queryKey: questionsKey });
        onChanged();
    };

    // Локальный порядок на время in-flight запроса (оптимистично), потом — ответ бэка.
    const [localOrder, setLocalOrder] = useState(null);
    const orderedQuestions = localOrder
        ? localOrder.map(id => questions.find(q => q.id === id)).filter(Boolean)
        : questions;
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleQuestionDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = orderedQuestions.map(q => q.id);
        const newIds = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
        setLocalOrder(newIds);
        show();
        try {
            const resp = await apiClient.put(Endpoints.questions.reorder(round.id), { orderedQuestionIds: newIds });
            if (Array.isArray(resp.data)) queryClient.setQueryData(questionsKey, resp.data);
            setLocalOrder(null);
            onChanged();
            enqueueSnackbar('Порядок вопросов сохранён', { variant: 'success' });
        } catch (error) {
            setLocalOrder(null);
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось изменить порядок вопросов'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleDeleteRound = (e) => {
        e.stopPropagation();
        openConfirmation(`Удалить раунд «${round.title}»? Все вопросы раунда тоже будут удалены.`, async () => {
            show();
            try {
                await apiClient.delete(Endpoints.rounds.delete(round.id));
                enqueueSnackbar('Раунд удалён', { variant: 'success' });
                onChanged();
            } catch (error) {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось удалить раунд'), { variant: 'error' });
            } finally {
                hide();
            }
        });
    };

    const handleAddQuestion = async () => {
        show();
        try {
            const fresh = await apiClient.get(Endpoints.questions.listForRound(round.id));
            const existing = Array.isArray(fresh.data) ? fresh.data : [];
            const orderId = existing.reduce((m, q) => Math.max(m, (q && q.orderId) || 0), 0) + 1;
            await apiClient.post(Endpoints.questions.createForRound(round.id), {
                orderId, type: QuestionType.SIMPLE, cost: 10, questionText: 'Новый вопрос', answerText: 'Ответ', kahoot: null
            });
            invalidateQuestions();
            enqueueSnackbar('Вопрос создан', { variant: 'success' });
        } catch (error) {
            if (error?.response?.data?.code === BusinessErrorCode.ORDER_ID_TAKEN) {
                enqueueSnackbar('Нумерация устарела, перезагрузите страницу', { variant: 'warning' });
            } else {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось создать вопрос'), { variant: 'error' });
            }
        } finally {
            hide();
        }
    };

    return (
        <article className={`round${expanded ? ' open' : ''}`}>
            <div className="round-row" onClick={onToggle}>
                {dragHandleProps && (
                    <span className="round-drag" {...dragHandleProps} onClick={(e) => e.stopPropagation()} title="Перетащить">
                        <DragDotsIcon />
                    </span>
                )}
                <div className="badge">{String(round.orderId).padStart(2, '0')}</div>
                <div className="name-block">
                    <div className="rname">
                        {round.title || '—'}
                        <span className="chip-ok">{displayCount} {pluralQuestions(displayCount)}</span>
                    </div>
                    <div className="rmeta">
                        <span>бюджет {timeLabel}</span>
                        {coverLabel && <><span className="dot">·</span><span>обложка: {coverLabel}</span></>}
                    </div>
                </div>
                <div className="actions">
                    <button type="button" className="icon-btn" title="Редактировать" onClick={(e) => { e.stopPropagation(); onEditRound(); }}>
                        <EditPencilIcon />
                    </button>
                    <button type="button" className="icon-btn danger" title="Удалить" onClick={handleDeleteRound}>
                        <TrashIcon />
                    </button>
                    <button type="button" className="icon-btn chev" title="Развернуть" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
                        <ChevronDownIcon />
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="detail">
                    <div className="detail-pad">
                        <div className="detail-grid">
                            <div className="dcover">
                                <div className="field-label">Обложка</div>
                                <div className="cover">
                                    {coverUrl ? <img src={coverUrl} alt={coverLabel || ''} /> : <span className="clabel">не задана</span>}
                                </div>
                            </div>
                            <div className="dinfo">
                                <div>
                                    <div className="field-label">Бюджет времени</div>
                                    <div className="time-big">{timeLabel}</div>
                                </div>
                                <div>
                                    <div className="field-label">Описание</div>
                                    <div className={`descr${round.description ? '' : ' empty'}`}>{round.description || 'нет описания'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="q-section">
                            <div className="q-head">
                                <h4 className="q-title">Вопросы</h4>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={handleAddQuestion}>
                                    <span className="ic"><PlusIcon /></span>Добавить вопрос
                                </button>
                            </div>
                            {questions.length === 0 ? (
                                <div className="q-empty">В этом раунде пока нет вопросов.</div>
                            ) : (
                                <div className="q-table-wrap">
                                    <table className="q-table">
                                        <thead>
                                            <tr><th className="td-drag" /><th>№</th><th>Тип</th><th>Стоимость</th><th>Вопрос</th><th>Ответ</th><th>Создан</th><th /></tr>
                                        </thead>
                                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd}>
                                            <SortableContext items={orderedQuestions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                                                <tbody>
                                                    {orderedQuestions.map(question => (
                                                        <SortableQuestionRow key={question.id} question={question} onEdit={setSelectedQuestion} />
                                                    ))}
                                                </tbody>
                                            </SortableContext>
                                        </DndContext>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {selectedQuestion && (
                <QuestionEditModal
                    key={selectedQuestion.id}
                    question={selectedQuestion}
                    onChanged={invalidateQuestions}
                    onClose={() => setSelectedQuestion(null)}
                />
            )}
        </article>
    );
}

function SortableQuestionRow({ question, onEdit }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    return (
        <tr ref={setNodeRef} style={style} className={isDragging ? 'dragging' : undefined}>
            <td className="td-drag" {...attributes} {...listeners}><DragDotsIcon /></td>
            <td className="num">{question.orderId}</td>
            <td>
                <span className={`q-type${question.type === QuestionType.KAHOOT ? '' : ' warm'}`}>
                    {question.type === QuestionType.KAHOOT ? 'Kahoot' : 'Стандартный'}
                </span>
            </td>
            <td className="cost">{question.cost}</td>
            <td className="q-cell">{question.questionText}</td>
            <td className="q-ans">{question.answerText}</td>
            <td className="q-date">{question.createdAt ? dayjs(question.createdAt).format('YYYY-MM-DD HH:mm') : '—'}</td>
            <td className="td-edit">
                <button type="button" className="icon-btn sm accent" title="Редактировать" onClick={() => onEdit(question)}>
                    <EditPencilIcon />
                </button>
            </td>
        </tr>
    );
}

SortableQuestionRow.propTypes = {
    question: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired
};

RoundItem.propTypes = {
    round: PropTypes.shape({
        id: PropTypes.number.isRequired,
        orderId: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        timeBudgetSeconds: PropTypes.number,
        coverMedia: PropTypes.object
    }).isRequired,
    questionCount: PropTypes.number,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onEditRound: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired,
    dragHandleProps: PropTypes.object
};

RoundItem.defaultProps = { questionCount: 0, dragHandleProps: null };
